import './assets/main.css'
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import { createRouter, createWebHistory, createMemoryHistory } from 'vue-router'
import { createPublicRoutes, createAdminRoutes } from './router'

// CSS string (iba pre prípad, že sme v embede a chceme ho adoptovať do ShadowRoot-u)
import tailwindCss from '../../assets/css/output.css?raw'
import { isEmbedded } from '@/utils/embed'

const embedded = isEmbedded()

/**
 * Ak dostaneme host (selektor), vytvorí ShadowRoot a vráti mount element v ňom.
 * POZOR: používa sa len v prípade, že embed beží bez widgetu a chceme si
 * Shadow DOM spravovať sami (fallback).
 */
function mountInShadow(hostSelector: string): HTMLElement {
  const host = document.querySelector(hostSelector) as HTMLElement | null
  if (!host) throw new Error(`Mount host not found: ${hostSelector}`)

  const shadow = host.attachShadow({ mode: 'open' })
  const mountEl = document.createElement('div')
  shadow.appendChild(mountEl)

  adoptCssIntoShadow(shadow, tailwindCss)

  return mountEl
}

/**
 * Bezpečne adoptuje CSS do shadow root-u (adoptedStyleSheets alebo <style> fallback).
 */
function adoptCssIntoShadow(shadow: ShadowRoot, css: string) {
  // Použi vlastnosť na ShadowRoot (nie Document.prototype)
  if ('adoptedStyleSheets' in shadow && typeof (window as any).CSSStyleSheet !== 'undefined') {
    const sheet = new (window as any).CSSStyleSheet()
    sheet.replaceSync(css)
    // @ts-ignore
    shadow.adoptedStyleSheets = [...(shadow.adoptedStyleSheets || []), sheet]
  } else {
    const style = document.createElement('style')
    style.textContent = css
    shadow.appendChild(style)
  }
}

/**
 * Ak bežíme na FE, potrebujeme base pre history podľa /designer mountu.
 */
function computeBase(): string {
  const MOUNT = '/designer'
  const p = window.location.pathname
  const i = p.indexOf(MOUNT)
  if (i === -1) return MOUNT
  const prefix = p.substring(0, i) // napr. '' alebo '/cs'
  return `${prefix}${MOUNT}`
}

// --- Router setup ---
const routes = embedded ? createAdminRoutes() : createPublicRoutes()
const history = embedded ? createMemoryHistory() : createWebHistory(computeBase())
const router = createRouter({ history, routes })

router.beforeEach((to, _from, next) => {
  // ak by sa admin route náhodou dostala do FE buildu
  if (to.meta?.adminOnly && !embedded) return next({ name: 'NotFound' })
  next()
})

// --- Mount target rozhodovanie ---
const mountTargetCandidate = (window as any).DESIGNER_MOUNT || '#app'
let mountEl: HTMLElement

if (embedded) {
  if (mountTargetCandidate instanceof HTMLElement) {
    // Widget nám poslal priamo element (v Shadow DOM-e).
    mountEl = mountTargetCandidate

    // Ak je mount v ShadowRoot-e, adoptuj CSS do tohto shadowu.
    const rootNode = mountEl.getRootNode() as Document | ShadowRoot
    // @ts-ignore: detect shadow root
    if (rootNode && (rootNode as ShadowRoot).host) {
      adoptCssIntoShadow(rootNode as ShadowRoot, tailwindCss)
    }
  } else {
    // Ako fallback – ak by niekde predsa len posielali selektor,
    // spravíme vlastný Shadow DOM a vraciame mount element.
    mountEl = mountInShadow(mountTargetCandidate as string)
  }
} else {
  // FE: očakávame selektor alebo element
  mountEl =
    mountTargetCandidate instanceof HTMLElement
      ? mountTargetCandidate
      : (document.querySelector(mountTargetCandidate as string) as HTMLElement)

  if (!mountEl) {
    throw new Error(`Mount element not found: ${mountTargetCandidate}`)
  }
}

// --- Vue app ---
const app = createApp(App)
app.use(createPinia())
app.use(router)

router.isReady().then(() => {
  if (embedded) {
    const current = router.currentRoute.value
    if (!current.matched.length) {
      const productId = (window as any).DESIGNER_BOOTSTRAP?.productId ?? 0
      router.replace(`/admin/${productId}`)
    }
  }
})

// Vue 3: .mount akceptuje priamo HTMLElement
app.mount(mountEl)
